import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const VideoCallModal = ({ bookingId, userId, userRole, onClose }) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);

  // WebRTC configuration
  const configuration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  useEffect(() => {
    return () => {
      endCall();
    };
  }, []);

  const startCall = async () => {
    try {
      setIsConnecting(true);
      
      // Get local media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Create peer connection
      const peerConnection = new RTCPeerConnection(configuration);
      peerConnectionRef.current = peerConnection;

      // Add local stream tracks to peer connection
      stream.getTracks().forEach(track => {
        peerConnection.addTrack(track, stream);
      });

      // Handle incoming tracks from remote peer
      peerConnection.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
      };

      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          // Send ICE candidate to other peer via signaling server
          sendSignal({
            booking_id: bookingId,
            from_user: userId,
            to_user: 'other_user_id', // Would be determined from booking
            type: 'ice-candidate',
            data: event.candidate
          });
        }
      };

      // Create offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      // Send offer to other peer
      await sendSignal({
        booking_id: bookingId,
        from_user: userId,
        to_user: 'other_user_id',
        type: 'offer',
        data: offer
      });

      setIsCallActive(true);
      setIsConnecting(false);
    } catch (error) {
      console.error('Error starting call:', error);
      setIsConnecting(false);
      alert('Failed to start video call. Please check camera/microphone permissions.');
    }
  };

  const sendSignal = async (signalData) => {
    try {
      await axios.post('/communication/video/signal', signalData);
    } catch (error) {
      console.error('Failed to send signal:', error);
    }
  };

  const endCall = () => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }

    setIsCallActive(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4">
        {/* Header */}
        <div className="bg-orange-600 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-2xl">📹</span>
            <div>
              <h2 className="text-xl font-bold">Video Call</h2>
              <p className="text-sm text-orange-100">
                {isCallActive ? 'Call in progress' : 'Ready to connect'}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              endCall();
              onClose();
            }}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Video Streams */}
        <div className="relative bg-gray-900" style={{ height: '480px' }}>
          {/* Remote Video (Main) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />

          {/* Local Video (Picture-in-Picture) */}
          <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-white shadow-lg">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
          </div>

          {/* Call Status Overlay */}
          {!isCallActive && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-90">
              <div className="text-center text-white">
                <div className="text-6xl mb-4">📹</div>
                <h3 className="text-2xl font-bold mb-2">Ready to start video call</h3>
                <p className="text-gray-300 mb-6">
                  Click the button below to connect with {userRole === 'customer' ? 'your chef' : 'the customer'}
                </p>
              </div>
            </div>
          )}

          {/* Connecting Overlay */}
          {isConnecting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-75">
              <div className="text-center text-white">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-white mx-auto mb-4"></div>
                <p className="text-xl">Connecting...</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="p-6 bg-gray-100 rounded-b-lg flex justify-center gap-4">
          {!isCallActive ? (
            <button
              onClick={startCall}
              disabled={isConnecting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-8 py-3 rounded-lg font-semibold flex items-center gap-2"
            >
              <span className="text-xl">📞</span>
              {isConnecting ? 'Connecting...' : 'Start Call'}
            </button>
          ) : (
            <>
              <button
                onClick={() => {
                  const videoTrack = localStreamRef.current?.getVideoTracks()[0];
                  if (videoTrack) {
                    videoTrack.enabled = !videoTrack.enabled;
                  }
                }}
                className="bg-gray-700 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-semibold"
              >
                📷 Toggle Video
              </button>
              <button
                onClick={() => {
                  const audioTrack = localStreamRef.current?.getAudioTracks()[0];
                  if (audioTrack) {
                    audioTrack.enabled = !audioTrack.enabled;
                  }
                }}
                className="bg-gray-700 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-semibold"
              >
                🎤 Toggle Audio
              </button>
              <button
                onClick={() => {
                  endCall();
                  onClose();
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2"
              >
                <span className="text-xl">📞</span>
                End Call
              </button>
            </>
          )}
        </div>

        {/* Instructions */}
        <div className="px-6 pb-4 text-sm text-gray-600">
          <p>
            <strong>Note:</strong> This feature requires camera and microphone permissions. 
            Both parties need to be online for the call to connect.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VideoCallModal;
